// src/components/HomeHero.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import videoSmMd from '../assets/sonic videos/0215.mp4';
import videoLg from '../assets/sonic videos/0215.mp4';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2, delayChildren: 0.3 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: "easeOut" }
    }
};

const badgeVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.7, ease: "easeOut" }
    }
};

const HomeHero = () => {
    const { t } = useTranslation('hero');

    return (
        <section className="relative min-h-screen w-full overflow-hidden flex items-center">
            {/* Background Videos */}
            <video
                className="absolute inset-0 w-full h-full object-cover lg:hidden"
                autoPlay
                loop
                muted
                playsInline
            >
                <source src={videoSmMd} type="video/mp4" />
            </video>

            <video
                className="absolute inset-0 w-full h-full object-cover hidden lg:block"
                autoPlay
                loop
                muted
                playsInline
            >
                <source src={videoLg} type="video/mp4" />
            </video>

            {/* Overlay - more cinematic */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#001F3F]/90 via-black/60 to-black/40" />

            {/* Content */}
            <div className="relative z-10 w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-24 py-16 md:py-20 lg:py-0">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto flex flex-col items-center lg:items-start text-center lg:text-left"
                >
                    {/* Floating Badge / Label */}
                    <motion.div
                        variants={badgeVariants}
                        className="mb-6 inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-lg rounded-full border border-white/20 text-[#7FDBFF] text-sm sm:text-base font-medium tracking-wider uppercase"
                    >
                        <span className="w-2 h-2 rounded-full bg-[#7FDBFF] animate-pulse"></span>
                        {t('badge') || 'Sri Lanka Awaits'}
                    </motion.div>

                    {/* Main Title */}
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-none"
                    >
                        <span className="text-white block">{t('title.line1')}</span>
                        <span className="bg-gradient-to-r from-[#0074D9] to-[#7FDBFF] bg-clip-text text-transparent block mt-1 md:mt-3">
                            {t('title.line2')}
                        </span>
                        <span className="text-[#7FDBFF]/90 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light italic block mt-3 md:mt-6">
                            {t('title.line3')}
                        </span>
                    </motion.h1>

                    {/* Subtitle */}
                    <motion.p
                        variants={itemVariants}
                        className="mt-8 md:mt-10 text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-100/90 font-light max-w-3xl leading-relaxed"
                    >
                        {t('subtitle')}
                        <span className="text-[#7FDBFF] font-normal"> {t('subtitle2')}</span>
                    </motion.p>

                    {/* CTA */}
                    <motion.div
                        variants={itemVariants}
                        className="mt-10 md:mt-14"
                    >
                        <a
                            href="/tour"
                            className="group relative inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 
                       bg-gradient-to-r from-[#0074D9]/20 to-[#7FDBFF]/10 
                       backdrop-blur-md border border-[#7FDBFF]/30 
                       rounded-full text-white font-semibold text-lg sm:text-xl tracking-wide
                       hover:border-[#7FDBFF]/60 hover:from-[#0074D9]/30 hover:to-[#7FDBFF]/20
                       transition-all duration-500 shadow-xl shadow-[#0074D9]/20
                       overflow-hidden"
                        >
                            <span className="relative z-10">{t('cta')}</span>

                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent 
                             -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

                            <svg className="w-6 h-6 relative z-10 group-hover:translate-x-1.5 transition-transform"
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </a>
                    </motion.div>

                    {/* Trust line */}
                    <motion.p
                        variants={itemVariants}
                        className="mt-10 text-base sm:text-lg text-gray-300/80 font-medium tracking-wide"
                    >
                        {t('trust')}
                    </motion.p>
                </motion.div>
            </div>

            {/* Scroll Indicator - cleaner version */}
            <div className="absolute bottom-8 sm:bottom-10 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2">
                <span className="text-xs uppercase tracking-widest text-[#7FDBFF]/70 font-medium">
                    Scroll
                </span>
                <div className="w-6 h-10 border-2 border-[#7FDBFF]/40 rounded-full flex items-center justify-center">
                    <div className="w-1.5 h-3 bg-[#7FDBFF]/70 rounded-full animate-bounce-slow"></div>
                </div>
            </div>
        </section>
    );
};

export default HomeHero;